import { useState } from "react";
import { FormReviewData, DestinationHealthInfo } from "@/types";
import { HealthRecommendations } from "@/components/common";
import MalariaRisk from "./MalariaRisk";

interface FloatingHealthRecommendationsProps {
  formData: FormReviewData;
  destinationInfoMap: Record<string, DestinationHealthInfo>;
  loadingDestinations: boolean;
}

export function FloatingHealthRecommendations({
  formData,
  destinationInfoMap,
  loadingDestinations,
}: FloatingHealthRecommendationsProps) {
  const [isOpen, setIsOpen] = useState(true); // Start open on desktop
  const [selectedDestinationIndex, setSelectedDestinationIndex] = useState(0);

  const trip = formData?.tripDetails;
  if (!trip?.destinations || trip.destinations.length === 0) return null;

  const destinations = trip.destinations
    .sort((a, b) => a.order - b.order)
    .filter((dest) => destinationInfoMap[dest.destination_country]);

  if (destinations.length === 0 && !loadingDestinations) return null;

  const currentDestination = destinations[selectedDestinationIndex];
  const currentInfo = currentDestination
    ? destinationInfoMap[currentDestination.destination_country]
    : null;

  const getTotalRecommendations = (info: DestinationHealthInfo) => {
    const requiredVaccines =
      info.health_recommendations?.vaccine_details?.required?.length || 0;
    const recommendedVaccines =
      info.health_recommendations?.vaccine_details?.recommended?.length || 0;
    const conditionalVaccines =
      info.health_recommendations?.vaccine_details?.conditional?.length || 0;
    const medications = info.health_recommendations?.medications?.length || 0;
    const malariaRisk = info.health_recommendations?.malaria_risk
      ?.prophylaxis_recommended
      ? 1
      : 0;

    return (
      requiredVaccines +
      recommendedVaccines +
      conditionalVaccines +
      medications +
      malariaRisk
    );
  };

  const getCriticalCount = (info: DestinationHealthInfo) => {
    return (
      info.health_recommendations?.vaccine_details?.required?.filter(
        (v) => v.risk_level === "critical"
      ).length || 0
    );
  };

  return (
    <>
      {/* Responsive Styles */}
      <style>{`
        /* Mobile - Hide completely */
        @media (max-width: 767px) {
          .health-side-panel {
            display: none !important;
          }
          .side-panel-toggle {
            display: none !important;
          }
        }

        /* Tablet (iPad Mini/Air) - 60% width */
        @media (min-width: 768px) and (max-width: 1023px) {
          .health-side-panel {
            display: ${isOpen ? "flex" : "none"} !important;
            width: 60vw !important;
            min-width: 380px !important;
          }
          .side-panel-toggle {
            display: flex !important;
            right: ${isOpen ? "calc(60vw + 8px)" : "12px"} !important;
            width: 50px !important;
            height: 90px !important;
          }
          .destination-tab-content {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 8px !important;
          }
          .destination-tab-badges {
            width: 100% !important;
            justify-content: flex-start !important;
          }
        }

        /* iPad Pro / Small Desktop - 45% width */
        @media (min-width: 1024px) and (max-width: 1279px) {
          .health-side-panel {
            display: ${isOpen ? "flex" : "none"} !important;
            width: 45vw !important;
            min-width: 380px !important;
          }
          .side-panel-toggle {
            display: flex !important;
            right: ${isOpen ? "calc(45vw + 12px)" : "16px"} !important;
            width: 55px !important;
            height: 100px !important;
          }
          .destination-tab-content {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 8px !important;
          }
          .destination-tab-badges {
            width: 100% !important;
            justify-content: flex-start !important;
          }
        }

        /* Medium Desktop - 35% width */
        @media (min-width: 1280px) and (max-width: 1535px) {
          .health-side-panel {
            display: ${isOpen ? "flex" : "none"} !important;
            width: 35vw !important;
            min-width: 400px !important;
          }
          .side-panel-toggle {
            display: flex !important;
            right: ${isOpen ? "calc(35vw + 16px)" : "18px"} !important;
          }
        }

        /* Large Desktop - 25% width */
        @media (min-width: 1536px) {
          .health-side-panel {
            display: ${isOpen ? "flex" : "none"} !important;
            width: 25vw !important;
            min-width: 380px !important;
          }
          .side-panel-toggle {
            display: flex !important;
            right: ${isOpen ? "calc(25vw + 20px)" : "20px"} !important;
          }
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Toggle Button - Responsive */}
      <button
        className={`side-panel-toggle ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: "fixed",
          right: "20px",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 150,
          width: "60px",
          height: "120px",
          background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
          border: "3px solid white",
          borderRadius: "12px 0 0 12px",
          boxShadow: "-4px 4px 12px rgba(20, 184, 166, 0.3)",
          cursor: "pointer",
          display: "none",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
          padding: "12px",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-50%) scale(1.05)";
          e.currentTarget.style.boxShadow =
            "-6px 6px 16px rgba(20, 184, 166, 0.4)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(-50%) scale(1)";
          e.currentTarget.style.boxShadow =
            "-4px 4px 12px rgba(20, 184, 166, 0.3)";
        }}
        title={
          isOpen ? "Hide Health Recommendations" : "Show Health Recommendations"
        }
      >
        <span style={{ fontSize: "32px", color: "white", lineHeight: 1 }}>
          {isOpen ? "→" : "🌍"}
        </span>
        {!isOpen && destinations.length > 0 && (
          <span
            style={{
              fontSize: "14px",
              color: "white",
              fontWeight: "700",
              marginTop: "8px",
            }}
          >
            {destinations.length}
          </span>
        )}
      </button>

      {/* Side Panel - Responsive */}
      {isOpen && (
        <div
          className="health-side-panel"
          style={{
            position: "fixed",
            top: "10vh",
            right: 0,
            height: "80vh",
            width: "25vw",
            zIndex: 100,
            background: "white",
            boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.15)",
            display: "flex",
            flexDirection: "column",
            animation: "slideIn 0.3s ease-out",
            borderRadius: "12px 0 0 12px",
          }}
        >
          <div
            style={{
              background: "white",
              height: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Panel Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
                padding: "16px 20px",
                borderBottom: "3px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "12px 0 0 0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2
                    style={{
                      margin: 0,
                      color: "white",
                      fontSize: "18px",
                      fontWeight: "700",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>🌍</span>
                    Health Info
                  </h2>
                  <p
                    style={{
                      margin: "2px 0 0 28px",
                      color: "rgba(255, 255, 255, 0.9)",
                      fontSize: "11px",
                    }}
                  >
                    Destination requirements
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "2px solid white",
                    borderRadius: "8px",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    padding: 0,
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "white";
                    e.currentTarget.style.transform = "scale(1.1)";
                    const span = e.currentTarget.querySelector("span");
                    if (span) span.style.color = "#14b8a6";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.2)";
                    e.currentTarget.style.transform = "scale(1)";
                    const span = e.currentTarget.querySelector("span");
                    if (span) span.style.color = "white";
                  }}
                  title="Close panel"
                >
                  <span
                    style={{
                      fontSize: "24px",
                      color: "white",
                      lineHeight: 1,
                      fontWeight: "bold",
                    }}
                  >
                    ×
                  </span>
                </button>
              </div>
            </div>

            {/* Destination Tabs - Compact for sidebar */}
            {destinations.length > 1 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  padding: "12px 16px",
                  background: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {destinations.map((dest, index) => {
                  const info = destinationInfoMap[dest.destination_country];
                  const criticalCount = getCriticalCount(info);
                  const isActive = index === selectedDestinationIndex;

                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedDestinationIndex(index)}
                      style={{
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: isActive
                          ? "2px solid #14b8a6"
                          : "2px solid #e5e7eb",
                        background: isActive ? "#f0fdfa" : "white",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "all 0.2s ease",
                        width: "100%",
                        overflow: "hidden",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = "#d1d5db";
                          e.currentTarget.style.background = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = "#e5e7eb";
                          e.currentTarget.style.background = "white";
                        }
                      }}
                    >
                      <div
                        className="destination-tab-content"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "700",
                            color: isActive ? "#14b8a6" : "#374151",
                            textAlign: "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          {info.destination?.name || dest.destination_country}
                        </span>
                        <div
                          className="destination-tab-badges"
                          style={{
                            display: "flex",
                            gap: "4px",
                            alignItems: "center",
                            flexShrink: 0,
                            flexWrap: "wrap",
                          }}
                        >
                          {criticalCount > 0 && (
                            <span
                              style={{
                                padding: "3px 8px",
                                borderRadius: "6px",
                                fontSize: "10px",
                                fontWeight: "700",
                                background: "#fee2e2",
                                color: "#dc2626",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ⚠️ {criticalCount} Required
                            </span>
                          )}
                          <span
                            style={{
                              padding: "3px 8px",
                              borderRadius: "6px",
                              fontSize: "10px",
                              fontWeight: "600",
                              background: isActive ? "#14b8a6" : "#f3f4f6",
                              color: isActive ? "white" : "#6b7280",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getTotalRecommendations(info)} Total
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Panel Content - Adjusted for narrow sidebar */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px",
                background: "#f9fafb",
              }}
            >
              {loadingDestinations ? (
                <div
                  style={{
                    padding: "40px 16px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      border: "3px solid #e5e7eb",
                      borderTop: "3px solid #14b8a6",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  <p
                    style={{
                      marginTop: "12px",
                      color: "#6b7280",
                      fontSize: "13px",
                      fontWeight: "500",
                    }}
                  >
                    Loading health recommendations...
                  </p>
                </div>
              ) : currentInfo ? (
                <>
                  <MalariaRisk destination={currentInfo.destination} />
                  <HealthRecommendations destination={currentInfo} />
                </>
              ) : (
                <div
                  style={{
                    padding: "40px 16px",
                    textAlign: "center",
                    background: "white",
                    borderRadius: "8px",
                  }}
                >
                  <span style={{ fontSize: "36px" }}>📋</span>
                  <p
                    style={{
                      marginTop: "12px",
                      color: "#6b7280",
                      fontSize: "13px",
                    }}
                  >
                    No health recommendations available
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
